// src/data/deadlineAlerts.js
//
// DEADLINE ALERTS (Bob 2026-10-14, "Address, fix and finish 3-6", suggestion 4: deadline alerts by e-mail). The platform
// already KNOWS every clock on a claim - the filing window, the carrier's answer, the appeal ladder, the Board - and shows
// them on the desks (deskClocks.js) and the union calendar (unionCalendar.deadlinesFromClaims). Nothing told anyone a day
// was coming unless they opened the screen. This module decides, for one run of the scheduled alertDeadlines function
// (functions\index.js), which clocks have reached an alert point and who is told. Pure - no Firestore, no React - proved by
// scripts\test-deadline-alerts.mjs, and bundled for the server by scripts\bundle-server-modules.js (one source, never two).
//
// NOTHING IS COMPUTED AFRESH. Every date is the one the calendar already reads; ruling 314's day count is inside it.
// THE PLATFORM DECIDES NO TIMELINESS QUESTION (claimRequirements E3): an alert says what day the agreement's count
// reaches and what is recorded - never that a claim is allowed, barred or late in law.
//
// WHEN:   the Organization's own clocks (and any clock with no party) at 7, 3 and 1 day(s) left, on the day itself, and
//         once in the three days after it passes with nothing recorded. The carrier's clocks once, in the three days after
//         the carrier's last day passes with no answer recorded - the officer then decides what follows. A clock first seen
//         inside a window gets that window's alert only; each (claim, clock, window) alerts ONCE (the function keeps a
//         deadlineAlerts/{id} record and creates it in the same transaction as the notices).
// WHO:    the filing window on a DRAFT - the member alone (ruling 56: no officer reads a draft). On a filed claim not yet
//         served on the carrier - the member, the Local Chairperson of its territory and the General Chairperson of its
//         committee. Every other clock - the two officers.
// HOW:    an in-app notice, always (ruling 14: the notification centre is the deliverable). An e-mail as well, to the same
//         people, only when the administrator has switched e-mail on (platformSettings/email.deadlineAlerts) - ruling 14's
//         "later, optional adapter behind the same interface". The e-mail is a document in mail/ in the shape the Firebase
//         "Trigger Email" extension sends; without the extension installed nothing leaves the platform.
import { deadlinesFromClaims } from './unionCalendar';
import { isPresented } from './claimPresentment';
import { noticeEmail } from './platformEmail';   // section 776: every e-mail is a notice with no content

const s = (v) => (v === undefined || v === null ? '' : String(v).trim());
const DAY = 86400000;
export const NOT_LIVE = ['closure', 'archived', 'suspended'];
export const UNION_WINDOWS = [7, 3, 1, 0];
export const AFTER_DAYS = 3;

// Days from now to the END of the last day (UTC), as the desks count it (deskClocks.daysLeft).
export function daysLeft(isoDate, nowMs) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s(isoDate))) return null;
  return Math.floor((Date.parse(isoDate + 'T23:59:59Z') - nowMs) / DAY);
}

// The window a clock is in today, or null. 'd7' 'd3' 'd1' 'today' 'passed' for the Organization's clocks; 'lapsed' for
// the carrier's.
export function windowFor(left, party) {
  if (left === null || left === undefined) return null;
  if (party === 'carrier') return (left < 0 && left >= -AFTER_DAYS) ? 'lapsed' : null;
  if (left < 0) return left >= -AFTER_DAYS ? 'passed' : null;
  if (left === 0) return 'today';
  if (left === 1) return 'd1';
  if (left <= 3) return 'd3';
  if (left <= 7) return 'd7';
  return null;
}

// Every clock on one claim. The calendar's own list, plus the filing window on a FILED claim not yet served - the window
// that closes Coyle's claim (YCP-GO342-2026-0013) on 2026-10-24 and appears on no clock list today: the calendar counts the
// window on drafts only, and the desks list it only for withdrawn claims. The stored expiresAt is the date (ruling 314 backfill).
export function clocksOf(c) {
  if (!c || NOT_LIVE.indexOf(s(c.status)) !== -1) return [];
  const out = deadlinesFromClaims([c]).map((d) => ({
    ...d,
    party: d.party || (/^Carrier answer due/.test(s(d.title)) ? 'carrier' : 'organization'),
    clock: /filing window/.test(s(d.title)) ? 'filing_window' : (/^Carrier answer due/.test(s(d.title)) ? 'carrier_answer' : (s(d.source) || 'appeal')),
  }));
  const exp = s(c.expiresAt).slice(0, 10);
  if (s(c.status) !== 'draft' && !isPresented(c) && /^\d{4}-\d{2}-\d{2}$/.test(exp)) {
    out.push({ kind: 'deadline', date: exp, title: 'Filing window closes on claim ' + (s(c.id) || s(c.title) || 'claim') + ' (service on the carrier is not recorded)', claimId: c.docId || c.id, party: 'organization', clock: 'filing_window' });
  }
  return out;
}

// A short, stable id for one clock on one claim in one window: what the function's deadlineAlerts/{id} record is keyed by.
export function alertId(claimId, clock, window) {
  const raw = s(clock.clock) + '|' + s(clock.date) + '|' + s(clock.title).replace(/claim \S+/g, 'claim');
  let h = 5381;
  for (let i = 0; i < raw.length; i++) h = ((h * 33) ^ raw.charCodeAt(i)) >>> 0;
  return s(claimId).replace(/[^A-Za-z0-9_-]/g, '_') + '_' + h.toString(16) + '_' + window;
}

// Who is told: [{ audience, recipientUid, committee, scopeKey, email? }] - audiences as notifications.js names them.
export function recipientsFor(c, clock) {
  const committee = s(c.committee || c.generalCommittee);
  const scopeKey = s(c.scopeKey);
  const member = s(c.claimantUid) || (s(c.status) === 'draft' ? s(c.createdBy) : '');
  const memberEmail = s(c.claimantEmail);
  const memberRow = (member || memberEmail) ? [{ audience: 'claimant', recipientUid: member || null, committee, scopeKey, email: memberEmail }] : [];
  if (s(c.status) === 'draft') return clock.clock === 'filing_window' ? memberRow : [];
  const officers = [];
  if (scopeKey) officers.push({ audience: 'local_chair', recipientUid: null, committee, scopeKey });
  if (committee) officers.push({ audience: 'general_chair', recipientUid: null, committee, scopeKey });
  return (clock.clock === 'filing_window' ? memberRow : []).concat(officers);
}

const WEEKDAY = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
export function dayName(iso) { const d = new Date(iso + 'T12:00:00Z'); return isNaN(d) ? '' : WEEKDAY[d.getUTCDay()]; }

// The words. Title and body for the notice; the e-mail carries the same and a link.
export function wordsFor(clock, window, left) {
  const when = dayName(clock.date) + ' ' + clock.date;
  const head = { d7: 'In ' + left + ' days', d3: 'In ' + left + ' day' + (left === 1 ? '' : 's'), d1: 'Tomorrow', today: 'Today', passed: 'Passed', lapsed: 'Carrier\'s time ran out' }[window] || 'Deadline';
  const title = head + ': ' + clock.title;
  let body;
  if (window === 'lapsed') {
    body = 'The carrier\'s last day, as the agreement counts it, was ' + when + '. No answer is recorded on the platform. '
      + 'What follows from that is for the officer handling the claim to decide; the platform decides no timeliness question.';
  } else if (window === 'passed') {
    body = 'The last day, as the agreement counts it, was ' + when + ', and nothing that stops this clock is recorded on the '
      + 'platform. If it was done and not recorded, record it on the claim; if it was not done, the officer handling the claim decides what follows.';
  } else {
    body = 'The last day, as the agreement counts it (day one is the day of the event), is ' + when + '. '
      + 'What counts is the day it is RECEIVED, shown by its proof.';
  }
  return { title, body };
}

// One run: every alert due now. claims: [{ id or docId, ...claim }]. Returns [{ id, claimId, window, left, clock,
// recipients, title, body }]. The function writes; this decides.
export function alertsDue(claims, nowMs) {
  const now = typeof nowMs === 'number' ? nowMs : Date.now();
  const out = [];
  for (const c of Array.isArray(claims) ? claims : []) {
    const claimId = s(c && (c.docId || c.id));
    if (!claimId) continue;
    for (const k of clocksOf(c)) {
      const left = daysLeft(k.date, now);
      const w = windowFor(left, k.party);
      if (!w) continue;
      const recipients = recipientsFor(c, k);
      if (!recipients.length) continue;
      out.push({ id: alertId(claimId, k, w), claimId, window: w, left, clock: k, recipients, ...wordsFor(k, w, left) });
    }
  }
  return out;
}

// The notice, in notifications.js base()'s shape (kind, audience, claimId, committee, scopeKey, recipientUid, channel,
// title, body, actorName, actorRole, createdAt, readAt) - so every centre that reads notices reads these.
export function noticeFor(alert, r, nowIso) {
  return {
    kind: 'deadline_alert', audience: r.audience, claimId: alert.claimId, committee: s(r.committee), scopeKey: s(r.scopeKey),
    recipientUid: r.recipientUid || null, channel: 'in_app', title: alert.title, body: alert.body,
    actorName: 'Deadline alerts', actorRole: 'system', createdAt: nowIso, readAt: null, deadlineAlertId: alert.id,
  };
}

// The e-mail, in the Trigger Email extension's shape: { to, message: { subject, text } }. The site and the platform's name
// are the installation's (ruling 312: no code in src names them) - the caller passes them.
export function emailFor(alert, to, installation) {
  const inst = installation || {};
  // Forensic audit 2026-10-16 (technical review): the subject carried alert.title, and on a DRAFT that title is the first
  // 60 characters of the member's own description (claimPayload.js draftTitle) - shown on lock screens and in shared
  // inboxes. The subject now says only when; what it is about is read on the platform. The in-app notice keeps its title.
  // Section 776 (Bob 2026-10-22): no content at all now - not even when. The alert itself is in the notification centre.
  return Object.assign(noticeEmail(to, inst, 'deadline'), { deadlineAlertId: alert.id });
}
